import { useDisclosure, useToast } from "@chakra-ui/react";
import { db, functions } from "connections/firebase";
import { updateDoc, doc } from "firebase/firestore";
import { FormikHelpers } from "formik";
import { houseConverter } from "interfaces/house";
import { IDevice, IDeviceFormPayload, TDeviceType } from "interfaces/device";
import { useEffect, useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { httpsCallable } from "firebase/functions";

const collection_name = "houses";

const initialValues: IDeviceFormPayload = {
  type: null,
  label: "",
  config: null,
};

export default function useDevice(houseId: string) {
  const toast = useToast();
  const [data, loading, error] = useDocumentData(
    doc(db, collection_name, houseId).withConverter(houseConverter),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const { onClose: onCloseForm, onOpen, isOpen: isOpenForm } = useDisclosure();
  const [formType, setFormType] = useState<TDeviceType | null>(null);

  const [formValue, setFormValue] = useState<{
    index: number;
    initialValues: IDeviceFormPayload;
  }>({
    index: -1,
    initialValues,
  });

  const [loadingAction, setLoadingAction] = useState<null | number>(null);
  const getInstalledDoorLock = httpsCallable(functions, "getInstalledDoorLock");

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const getDeviceDetail = (index: number) => {
    if (data?.devices) {
      const selected = data.devices[index];
      if (selected) return selected;
    }

    return null;
  };

  const getDoorLockList = async () => {
    const doorLockList = await getInstalledDoorLock();
    console.log(doorLockList);
  };

  const addDevice = async (params: IDevice) => {
    if (data) {
      setLoadingAction(-1);
      const houseData = { ...data };
      if (houseData.devices) {
        houseData.devices.push(params);
      } else {
        houseData.devices = [params];
      }
      await updateDoc(doc(db, collection_name, houseId), houseData);
      setLoadingAction(null);
    }
  };

  const updateDevice = async (index: number, params: IDevice) => {
    if (data) {
      setLoadingAction(index);
      const houseData = { ...data };
      houseData.devices[index] = params;
      await updateDoc(doc(db, collection_name, houseId), houseData);
      setLoadingAction(null);
    }
  };

  const deleteDevice = async (index: number) => {
    if (data) {
      setLoadingAction(index);
      const houseData = { ...data };
      houseData.devices.splice(index, 1);
      await updateDoc(doc(db, collection_name, houseId), houseData);
      setLoadingAction(null);
    }
  };

  const openEditForm = (index: number, formTypeParam: TDeviceType) => {
    onOpen();
    const initialValues = getDeviceDetail(index) as IDeviceFormPayload;
    setFormType(formTypeParam);
    setFormValue({
      index,
      initialValues,
    });
  };

  const openAddForm = (formTypeParam: TDeviceType) => {
    onOpen();
    setFormType(formTypeParam);
    let values = initialValues;
    if (formTypeParam === "lock") {
      values = {
        ...initialValues,
        type: "lock",
        config: { lockId: "", lockData: "" },
      };
    }
    setFormValue({
      index: -1,
      initialValues: values,
    });
  };

  const onSubmitForm = async (
    values: IDevice,
    formikHelpers: FormikHelpers<IDevice>
  ) => {
    // console.log(values);
    if (formValue.index === -1) {
      await addDevice(values);
    } else {
      await updateDevice(formValue.index, values);
    }

    onCloseForm();
    formikHelpers.resetForm();
  };

  return {
    data,
    loading,
    addDevice,
    updateDevice,
    deleteDevice,
    selectedId: loadingAction,
    isLoadingAction: loadingAction !== null,
    openAddForm,
    openEditForm,
    getDeviceDetail,
    formValue,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
    getDoorLockList,
    formType,
  };
}
